import {
  Coordinate2Result,
  MultiPolygon2,
  Point2,
  ResultType,
} from '../../geometric-types';

export enum BoundingBox2Position {
  MIN_X = 'min-x',
  MIN_Y = 'min-y',
  MAX_X = 'max-x',
  MAX_Y = 'max-y',
}

interface BoundingBox2Points {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  minXPoint: Point2;
  minYPoint: Point2;
  maxXPoint: Point2;
  maxYPoint: Point2;
}

function findBoundingBox2Points(vertices: Point2[]): BoundingBox2Points {
  const result: BoundingBox2Points = {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
    minXPoint: [0, 0],
    minYPoint: [0, 0],
    maxXPoint: [0, 0],
    maxYPoint: [0, 0],
  };

  for (const vertex of vertices) {
    const [x, y] = vertex;

    if (x < result.minX) {
      result.minX = x;
      result.minXPoint = vertex;
    }
    if (y < result.minY) {
      result.minY = y;
      result.minYPoint = vertex;
    }
    if (x > result.maxX) {
      result.maxX = x;
      result.maxXPoint = vertex;
    }
    if (y > result.maxY) {
      result.maxY = y;
      result.maxYPoint = vertex;
    }
  }

  return result;
}

function getCoordinateResult(
  points: BoundingBox2Points,
  position: BoundingBox2Position
): Coordinate2Result {
  switch (position) {
    case BoundingBox2Position.MIN_X:
      return {
        type: ResultType.COORDINATE,
        point: points.minXPoint,
        value: points.minX,
      };
    case BoundingBox2Position.MIN_Y:
      return {
        type: ResultType.COORDINATE,
        point: points.minYPoint,
        value: points.minY,
      };
    case BoundingBox2Position.MAX_X:
      return {
        type: ResultType.COORDINATE,
        point: points.maxXPoint,
        value: points.maxX,
      };
    case BoundingBox2Position.MAX_Y:
      return {
        type: ResultType.COORDINATE,
        point: points.maxYPoint,
        value: points.maxY,
      };
  }
}

export const calculateSheetLineShapeBoundingBox = (
  points: Point2[],
  position: BoundingBox2Position
): Coordinate2Result => {
  const boundingBoxPoints = findBoundingBox2Points(points);
  return getCoordinateResult(boundingBoxPoints, position);
};

export const calculateSheetPolygonShapeBoundingBox = (
  polygon: MultiPolygon2,
  position: BoundingBox2Position
): Coordinate2Result => {
  // Convert all exterior vertices to 3D points
  const allVertices: Point2[] = polygon.polygons.flatMap(
    (poly) => poly.exterior
  );

  const points = findBoundingBox2Points(allVertices);
  return getCoordinateResult(points, position);
};
